import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../services/api';
import { field, ghost, muted, rowBorder, errorText, plural, dayLabel } from '../../pages/time/styles';

const thisMonth = () => new Date().toISOString().slice(0, 7);

/**
 * HR / Admin: days people were absent without applying for leave and nobody has accounted for. HR picks the
 * days and gives a reason; the days go to the person's manager (an admin when there is no manager) as
 * Leave Without Pay. Nothing is taken off pay unless it is approved there.
 */
const AbsencesTab = ({ onProposed }) => {
    const [month, setMonth] = useState(thisMonth());
    const [people, setPeople] = useState([]);
    const [picked, setPicked] = useState({});   // userId -> array of day keys
    const [reasons, setReasons] = useState({}); // userId -> text
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState('');
    const latest = useRef(0); // the newest request: a slower, older answer (another month) must not replace it

    const load = useCallback(async () => {
        const mine = ++latest.current;
        const [year, m] = month.split('-').map(Number);
        if (!year || !m) { setPeople([]); setLoading(false); return; } // an emptied month box shows nothing, not the old month
        setLoading(true);
        setError('');
        try {
            const res = await api.get('/leaves/lwp/candidates', { params: { month: m, year } });
            if (mine !== latest.current) return;
            setPeople(res.data.people || []);
        } catch (e) {
            if (mine !== latest.current) return;
            setError(errorText(e, 'Could not load the absences.'));
            setPeople([]);
        } finally {
            if (mine === latest.current) setLoading(false);
        }
    }, [month]);

    useEffect(() => { load(); }, [load]);

    const toggle = (userId, day) => setPicked(prev => {
        const now = new Set(prev[userId] || []);
        if (now.has(day)) now.delete(day); else now.add(day);
        return { ...prev, [userId]: [...now].sort() };
    });

    const send = async (person) => {
        const days = picked[person.userId] || [];
        setSending(person.userId);
        setError('');
        setMessage('');
        try {
            await api.post('/leaves/lwp', { userId: person.userId, dates: days, reason: (reasons[person.userId] || '').trim() });
            setError('');
            setMessage(`${plural(days.length, 'day')} for ${person.name} sent for approval to ${person.hasManager ? (person.managerName || 'their manager') : 'an admin (they have no manager)'}.`);
            setPicked(prev => ({ ...prev, [person.userId]: [] }));
            setReasons(prev => ({ ...prev, [person.userId]: '' }));
            await load();
            onProposed?.();
        } catch (e) {
            setMessage('');
            setError(errorText(e, 'Could not send these days for approval.'));
        } finally {
            setSending('');
        }
    };

    return (
        <div className="glass-card" style={{ padding: '1.5rem' }}>
            <p style={{ ...muted, marginTop: 0 }}>
                Days someone was absent without applying for leave. Choose the days, give a reason and send them for approval as
                <strong> Leave Without Pay (LWP)</strong>. Their manager decides; nothing is taken off pay unless it is approved.
                Days with attendance, a holiday, a weekend or any leave request are not shown.
            </p>

            <label style={{ display: 'inline-block', marginBottom: '1rem' }}>
                Month{' '}
                <input type="month" aria-label="Month" value={month} max={thisMonth()} onChange={(e) => { setMonth(e.target.value); setMessage(''); setError(''); setPicked({}); setReasons({}); }} style={field} />
            </label>

            {message && <p role="status" style={{ color: '#047857' }}>{message}</p>}
            {error && <p role="alert" style={{ color: '#dc2626' }}>{error}</p>}
            {loading && <p style={muted}>Looking for absences…</p>}
            {!loading && !error && people.length === 0 && <p data-testid="no-absences" style={muted}>No unexplained absences in this month.</p>}

            {people.map(p => {
                const days = picked[p.userId] || [];
                const reason = reasons[p.userId] || '';
                return (
                    <section key={p.userId} data-testid={`absent-${p.userId}`} aria-label={`Absences of ${p.name}`} style={{ borderTop: rowBorder, padding: '1rem 0' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
                            <div>
                                <strong>{p.name}</strong>{p.department ? <span style={muted}> · {p.department}</span> : null}
                                <div style={muted}>
                                    {p.hasManager ? `Approves: ${p.managerName || 'their manager'}` : 'No manager: an admin approves'} · {plural(p.days.length, 'day')} not accounted for
                                </div>
                            </div>
                            <button type="button" style={ghost} onClick={() => setPicked(prev => ({ ...prev, [p.userId]: days.length === p.days.length ? [] : [...p.days] }))}>
                                {days.length === p.days.length ? 'Clear' : 'Select all'}
                            </button>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem 1rem', margin: '0.75rem 0' }}>
                            {p.days.map(d => (
                                <label key={d} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                    <input type="checkbox" checked={days.includes(d)} onChange={() => toggle(p.userId, d)} aria-label={`${p.name}, ${dayLabel(d, { weekday: 'short', day: 'numeric', month: 'short' })}`} />
                                    {dayLabel(d, { weekday: 'short', day: 'numeric', month: 'short' })}
                                </label>
                            ))}
                        </div>
                        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
                            <input
                                aria-label={`Reason for ${p.name}`}
                                placeholder="Reason (the employee and manager will read it)"
                                value={reason}
                                maxLength={500}
                                onChange={(e) => setReasons(prev => ({ ...prev, [p.userId]: e.target.value }))}
                                style={{ ...field, flex: '1 1 20rem' }}
                            />
                            <button type="button" className="btn btn-primary" disabled={!days.length || !reason.trim() || sending === p.userId} onClick={() => send(p)}>
                                {sending === p.userId ? 'Sending…' : `Send ${days.length ? plural(days.length, 'day') : ''} for approval`.replace('  ', ' ')}
                            </button>
                        </div>
                    </section>
                );
            })}
        </div>
    );
};

export default AbsencesTab;
